import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Permission Context
const PermissionContext = createContext(null);

export function PermissionProvider({ children }) {
  const { user } = useAuth();
  const [permissions, setPermissions] = useState([]);
  const [visibleMenus, setVisibleMenus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);

  const fetchPermissions = useCallback(async () => {
    const token = localStorage.getItem('token');
    
    if (!user || !token) {
      setPermissions([]);
      setVisibleMenus([]);
      setIsSuperAdmin(false);
      setLoading(false);
      return;
    }

    // Note: isSuperAdmin will be determined by backend based on assigned roles
    // No hardcoded user_id check here

    try {
      const response = await axios.get(`${API}/rbac/my-permissions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setPermissions(response.data.permissions || []);
      setVisibleMenus(response.data.visible_menus || []);
      // is_super_admin comes from backend based on assigned roles (not hardcoded user_id)
      setIsSuperAdmin(response.data.is_super_admin || false);
    } catch (error) {
      console.error('Failed to fetch permissions:', error);
      // Fallback: derive permissions from legacy role
      const legacyPermissions = getLegacyPermissions(user.role);
      setPermissions(legacyPermissions.permissions);
      setVisibleMenus(legacyPermissions.menus);
      // If legacy role is admin, check if they have super_admin in assigned_roles
      const hasSuperRole = (user.assigned_roles || []).includes('super_admin');
      setIsSuperAdmin(hasSuperRole);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchPermissions();
  }, [fetchPermissions]);

  // Check if user has a specific permission
  const hasPermission = useCallback((permissionCode) => {
    if (isSuperAdmin) return true;
    return permissions.includes(permissionCode);
  }, [permissions, isSuperAdmin]);

  // Check if user has any of the specified permissions
  const hasAnyPermission = useCallback((permissionCodes) => {
    if (isSuperAdmin) return true;
    return permissionCodes.some(code => permissions.includes(code));
  }, [permissions, isSuperAdmin]);

  // Check if user has all of the specified permissions
  const hasAllPermissions = useCallback((permissionCodes) => {
    if (isSuperAdmin) return true;
    return permissionCodes.every(code => permissions.includes(code));
  }, [permissions, isSuperAdmin]);

  // Check if a menu should be visible
  const canSeeMenu = useCallback((menuKey) => {
    if (isSuperAdmin) return true;
    return visibleMenus.includes(menuKey);
  }, [visibleMenus, isSuperAdmin]);

  // Refresh permissions (call after role changes)
  const refreshPermissions = useCallback(() => {
    setLoading(true);
    fetchPermissions();
  }, [fetchPermissions]);

  const value = {
    permissions,
    visibleMenus,
    loading,
    isSuperAdmin,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    canSeeMenu,
    refreshPermissions
  };

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
}

export function usePermissions() {
  const context = useContext(PermissionContext);
  if (!context) {
    throw new Error('usePermissions must be used within a PermissionProvider');
  }
  return context;
}

// Fallback: Legacy role to permissions mapping
function getLegacyPermissions(role) {
  const roleLower = (role || '').toLowerCase();
  
  const roleMapping = {
    admin: {
      permissions: [
        'view_orders', 'create_order', 'edit_order', 'delete_order',
        'assign_nakes', 'unassign_nakes', 'share_job', 'view_workflow', 'generate_surat',
        'view_invoices', 'create_invoice', 'edit_invoice', 'delete_invoice',
        'mark_invoice_paid', 'upload_payment_proof',
        'view_vouchers', 'generate_voucher', 'void_voucher',
        'view_supply_requests', 'create_supply_request', 'process_supply_request',
        'view_purchase_orders', 'create_purchase_order', 'edit_purchase_order', 'manage_po_payment',
        'view_logistic_returns', 'create_logistic_return', 'approve_logistic_return',
        'view_master_products', 'manage_master_products',
        'view_master_suppliers', 'manage_master_suppliers',
        'view_master_inventory', 'manage_master_inventory',
        'view_master_services', 'manage_master_services',
        'view_activity_log', 'view_active_users', 'view_reports', 'export_reports',
        'view_users', 'create_user', 'edit_user', 'delete_user',
        'view_nurses', 'manage_nurses',
        'manage_document_settings',
        'view_discussions', 'participate_discussions', 'view_nakes_chats',
        'view_schedule', 'view_medical_records', 'manage_medical_records'
      ],
      menus: [
        'dashboard', 'orders', 'nurses', 'schedule', 'discussions', 'job_discussions',
        'nakes_chats', 'invoices', 'vouchers', 'supply_requests', 'purchase_orders',
        'logistic_returns', 'medical_records', 'master_products', 'master_inventory',
        'master_suppliers', 'master_services', 'reports', 'activity_log', 'active_users',
        'users', 'document_settings'
      ]
    },
    marketing: {
      permissions: [
        'view_orders', 'create_order', 'edit_order',
        'assign_nakes', 'share_job', 'view_workflow', 'generate_surat',
        'view_invoices', 'create_invoice', 'edit_invoice',
        'mark_invoice_paid', 'upload_payment_proof',
        'view_vouchers', 'generate_voucher',
        'view_activity_log', 'view_active_users', 'view_reports',
        'view_discussions', 'participate_discussions',
        'view_schedule'
      ],
      menus: [
        'dashboard', 'orders', 'schedule', 'discussions',
        'invoices', 'vouchers', 'reports', 'activity_log', 'active_users'
      ]
    },
    finance: {
      permissions: [
        'view_invoices', 'edit_invoice', 'mark_invoice_paid', 'upload_payment_proof',
        'view_vouchers', 'generate_voucher', 'void_voucher',
        'view_activity_log', 'view_reports'
      ],
      menus: [
        'dashboard', 'invoices', 'vouchers', 'activity_log'
      ]
    },
    gudang: {
      permissions: [
        'view_supply_requests', 'process_supply_request',
        'view_purchase_orders', 'create_purchase_order', 'edit_purchase_order', 'manage_po_payment',
        'view_logistic_returns', 'approve_logistic_return',
        'view_master_products', 'manage_master_products',
        'view_master_suppliers', 'manage_master_suppliers',
        'view_master_inventory', 'manage_master_inventory',
        'view_discussions', 'participate_discussions', 'view_nakes_chats'
      ],
      menus: [
        'dashboard', 'supply_requests', 'purchase_orders', 'logistic_returns',
        'master_products', 'master_suppliers', 'master_inventory', 'nakes_chats'
      ]
    },
    nakes: {
      permissions: [
        'view_own_jobs', 'execute_job', 'upload_ic', 'submit_report', 'manage_own_supply',
        'view_discussions', 'participate_discussions'
      ],
      menus: [
        'jobs', 'assignments', 'my_supply_requests', 'discussions'
      ]
    }
  };

  return roleMapping[roleLower] || { permissions: [], menus: ['dashboard'] };
}

export default PermissionContext;
